import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';

import { VgApiService, VgCoreModule } from '@videogular/ngx-videogular/core';
import { VgControlsModule, VgFullscreenComponent } from '@videogular/ngx-videogular/controls';
import { VgOverlayPlayModule } from '@videogular/ngx-videogular/overlay-play';
import { VgBufferingModule } from '@videogular/ngx-videogular/buffering';

import { VideoSearchResult } from '../../model/video-search-result.model';
import { QualityControlSelectorComponent } from './addons/quality-control-selector/quality-control-selector.component';
import { VideoSource } from '../../model/video-source';
import { VideoMetadataComponent } from '../video-metadata/video-metadata.component';
import { VideoSceneResponse } from '@solverml/api';

@Component({
  selector: 'app-video-player',
  templateUrl: './video-player.component.html',
  styleUrls: ['./video-player.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    VgCoreModule,
    VgControlsModule,
    VgOverlayPlayModule,
    VgBufferingModule,
    QualityControlSelectorComponent,
    VideoMetadataComponent
  ]
})
export class VideoPlayerComponent implements OnInit, OnDestroy {
  private static readonly NATURAL_PLAYBACK_MAX_DELTA_SECONDS = 1.0;

  @Input() video: VideoSearchResult;
  @Input() activeScene: VideoSceneResponse | null;

  @Output() onPlayerReady = new EventEmitter<VgApiService>();
  @Output() onTimeUpdate = new EventEmitter<number>();
  @Output() onPlayingStatus = new EventEmitter<boolean>();

  @ViewChild(VgFullscreenComponent) vgFullScreenComponent!: VgFullscreenComponent;

  api: VgApiService = new VgApiService();
  selectedVideoSource: VideoSource = null;
  vgOverlayPlay: HTMLElement | null = null;

  isMouseHovering = false;
  private isHoveringPlayer = false;
  private isPlaying = false;
  private lastCurrentTime: number = 0;
  private hasAutoPaused: boolean = false;

  private timerInterval: any;

  constructor(
    private elementRef: ElementRef
  ) { }

  ngOnInit() {
    this.selectedVideoSource = this.video.media_urls[0];
    this.vgOverlayPlay = this.elementRef.nativeElement.querySelector('#vg-player');
  }

  ngOnDestroy() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  }


  handlePlayerReadyEvent(source: VgApiService) {
    this.api = source;
    this.onPlayerReady.emit(this.api);

    this.api.getDefaultMedia().subscriptions.canPlay.subscribe(() => {
    });

    this.api.pause();

    this.api.subscriptions.playing.subscribe(() => {
      this.isPlaying = true;
      this.onPlayingStatus.emit(true);
    });

    this.api.subscriptions.pause.subscribe(() => {
      this.isPlaying = false;
      this.onPlayingStatus.emit(false);
    });

    this.timerInterval = setInterval(() => {
      if (this.api && this.api.getDefaultMedia()) {
        const currentTime = this.api.getDefaultMedia().currentTime;

        this.onTimeUpdate.emit(currentTime);

        if (this.activeScene && this.isPlaying) {

          if (!this.hasAutoPaused) {
            const endTime = this.activeScene.end_time_in_seconds;
            const timeDelta = Math.abs(currentTime - this.lastCurrentTime);
            const isNaturalPlayback = timeDelta < VideoPlayerComponent.NATURAL_PLAYBACK_MAX_DELTA_SECONDS;
            const pauseThreshold = endTime - 0.1;

            if (currentTime >= pauseThreshold && isNaturalPlayback) {
              this.api.pause();
              const safeTime = Math.max(this.activeScene.start_time_in_seconds, endTime - 0.05);
              this.api.seekTime(safeTime);
              this.hasAutoPaused = true;
            }
          }
        }
        this.lastCurrentTime = currentTime;
      }
    }, 100);
  }

  public resetAutoPause(): void {
    this.hasAutoPaused = false;
  }

  public isFullScreen(): boolean {
    return this.vgFullScreenComponent?.isFullscreen || false;
  }

  public getVideoSources(): VideoSource[] {
    return this.video.media_urls
  }

  public setVideoSource(videoSource: VideoSource) {
    const time_in_seconds = this.getCurrentTime();
    const wasPlaying = this.isPlaying;

    this.selectedVideoSource = videoSource;
    setTimeout(() => {
      this.seekVideoTo(time_in_seconds);
      setTimeout(() => {
        if (wasPlaying) {
          this.api.play();
        } else {
          this.api.pause();
        }
      }, 10);
    });
  }

  public getCurrentTime(): number {
    if (this.api && this.api.getDefaultMedia()) {
      return this.api.getDefaultMedia().currentTime;
    }
    return 0;
  }

  public seekVideoTo(time_in_seconds: number): void {
    if (this.api && this.api.getDefaultMedia()) {
      this.api.getDefaultMedia().currentTime = time_in_seconds;
      this.lastCurrentTime = time_in_seconds;
    }
  }

  public showVideoOverlay(): boolean {
    return this.isMouseHovering || !this.isPlaying;
  }

  @HostListener('mouseenter')
  public mouseenterListener(): void {
    this.isHoveringPlayer = true;
    this.isMouseHovering = true;
  }

  @HostListener('mouseleave')
  public mouseleaveListener(): void {
    const delay_in_seconds = 2;
    this.isHoveringPlayer = false;
    setTimeout(() => {
      if (this.isHoveringPlayer) return;
      this.isMouseHovering = false;
    }, delay_in_seconds * 1000);
  }
}
